import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Ticket, Calendar, Gift, Plus, Trash2, Edit2, X, Check, Settings, BarChart3, Package as PackageIcon, Upload, Download, Copy, ChevronUp, ChevronDown, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import { Modal } from "../../components/shared/Modal";
import RefundAmountCell from "../../components/shared/RefundAmountCell";
import IssueTicketsTab, { PendingLinksPanel } from "./IssueTicketsTab";

const TABS = [
  { id: "ticket-types", label: "Ticket Tiers", icon: Ticket },
  { id: "packages", label: "Packages", icon: PackageIcon },
  { id: "events", label: "Events", icon: Calendar },
  { id: "tickets", label: "Sold Tickets", icon: Ticket },
  { id: "issue", label: "Issue Tickets", icon: Send },
  { id: "vouchers", label: "Vouchers", icon: Gift },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "settings", label: "Shop Settings", icon: Settings },
];

// iter162: group tiers/packages by their free-text `category` label. Items
// with an empty/whitespace category fall into a synthetic "Other" bucket
// that renders last. Category header order is set by the min
// `category_sort_order` among items sharing that category, then by label
// alphabetically. Within a group we preserve the incoming array order
// (backend already sorts by sort_order, created_at).
export const groupByCategory = (items) => {
  const UNCAT = "__uncategorized__";
  const buckets = new Map();
  (items || []).forEach(it => {
    const raw = ((it.category || "") + "").trim();
    const key = raw || UNCAT;
    if (!buckets.has(key)) buckets.set(key, { label: raw || "Other", items: [], minOrder: null, uncategorized: !raw });
    const b = buckets.get(key);
    b.items.push(it);
    const co = Number.isFinite(it.category_sort_order) ? it.category_sort_order : 0;
    if (b.minOrder === null || co < b.minOrder) b.minOrder = co;
  });
  const groups = [...buckets.entries()].map(([key, b]) => ({
    key, label: b.label, items: b.items, uncategorized: b.uncategorized,
    order: b.uncategorized ? Number.POSITIVE_INFINITY : (b.minOrder ?? 0),
  }));
  groups.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  return groups;
};

// ---------- Reusable Banner Uploader ----------
const BANNER_RECOMMENDED = {
  "shop-hero": "1920 × 600 px (16:5, wide banner)",
  "event-banner": "1920 × 600 px (16:5, wide banner)",
  "tier-banner": "400 × 400 px (square tile)",
  "package-banner": "400 × 400 px (square tile)",
  "tenant-logo": "400 × 400 px (square, transparent PNG preferred)",
  "generic": "1200 × 400 px",
};

export function BannerUpload({ authAxios, value, onChange, purpose = "generic", label = "Banner" }) {
  const [uploading, setUploading] = useState(false);
  const rec = BANNER_RECOMMENDED[purpose] || BANNER_RECOMMENDED.generic;
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await authAxios.post(`/shop-admin/upload-image?purpose=${encodeURIComponent(purpose)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        {value ? (
          <img src={value} alt="banner" className="w-16 h-16 object-contain bg-slate-50 rounded-lg border" />
        ) : (
          <div className="w-16 h-16 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-300">
            <Upload className="w-5 h-5" />
          </div>
        )}
        <label className="cursor-pointer flex items-center gap-1 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-medium text-slate-700" data-testid={`banner-upload-${purpose}`}>
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : (value ? "Replace" : "Upload")}
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={uploading} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="text-red-500 text-xs hover:underline">Remove</button>}
      </div>
      <p className="text-[10px] text-slate-400 mt-1">Recommended: {rec}. Uploads are shown uncropped (contain).</p>
    </div>
  );
}

// ---------- Reusable Discount Editor ----------
export function DiscountEditor({ value, onChange }) {
  const v = value || { type: "", value: 0 };
  return (
    <div className="flex gap-2 items-end">
      <div className="flex-1">
        <label className="text-xs text-slate-500">Discount type</label>
        <select
          value={v.type || ""}
          onChange={e => onChange(e.target.value ? { type: e.target.value, value: v.value || 0 } : null)}
          className="w-full px-3 py-2 rounded-xl border text-sm"
        >
          <option value="">None</option>
          <option value="percent">Percent (%)</option>
          <option value="fixed">Fixed (€)</option>
        </select>
      </div>
      {v.type && (
        <div className="w-28">
          <label className="text-xs text-slate-500">Value</label>
          <input
            type="number" step="0.01" min="0"
            value={v.value ?? ""}
            onChange={e => onChange({ type: v.type, value: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 rounded-xl border text-sm"
          />
        </div>
      )}
    </div>
  );
}


// Applies discount dict to a base price (mirror of backend _apply_discount).
function _applyDisc(base, disc) {
  if (!disc || !disc.type || !disc.value) return Math.max(0, base);
  if (disc.type === "percent") return Math.max(0, base * (1 - Math.min(100, disc.value) / 100));
  if (disc.type === "fixed") return Math.max(0, base - disc.value);
  return base;
}

/**
 * Unified Audience & Pricing editor.
 * One row per potential buyer type: Guest + each Member Type.
 * Each row: [checkbox to allow buying] + [discount type/value] + [effective price preview].
 * Selected audiences without a discount inherit the base price.
 */
export function AudienceAndPricingEditor({ audience, memberPrices, memberTypes, basePrice, baseDiscount, onChange }) {
  const guestBase = _applyDisc(Number(basePrice) || 0, baseDiscount);
  // "Unregistered" is our anonymous-shopper audience (no login / OTP-only guest checkout).
  // "Guest" is a registered guest with an account. Member types follow.
  const audienceRows = [
    { id: "unregistered", label: "Unregistered", desc: "Anonymous shoppers (no login) or guest-OTP checkout" },
    { id: "guest", label: "Guest", desc: "Registered guests with an account" },
    ...(memberTypes || []).map(m => ({ id: m.id, label: `Member: ${m.name}`, desc: "" })),
  ];
  const mpById = Object.fromEntries((memberPrices || []).map(r => [r.member_type_id, r]));

  // Local state so a picked type persists even when the numeric value is still 0.
  const [drafts, setDrafts] = useState({});
  const getDisc = (id) => drafts[id] || mpById[id]?.discount || { type: "", value: 0 };

  // Merge server-provided memberPrices with local drafts so editing one row's
  // discount never wipes another row's already-saved discount.
  const buildNextMp = (drf) => {
    const merged = {};
    // seed with any existing member prices from props (untouched rows).
    for (const mp of (memberPrices || [])) {
      if (mp?.discount?.type && Number(mp.discount.value) > 0) {
        merged[mp.member_type_id] = {
          member_type_id: mp.member_type_id,
          discount: { type: mp.discount.type, value: Number(mp.discount.value) },
          visible_in_shop: mp.visible_in_shop !== false,
        };
      }
    }
    // Overlay/replace with any active drafts (which include user edits, incl. clearing).
    for (const [id, d] of Object.entries(drf || {})) {
      if (d && d.type && Number(d.value) > 0) {
        merged[id] = {
          member_type_id: id,
          discount: { type: d.type, value: Number(d.value) },
          visible_in_shop: mpById[id]?.visible_in_shop !== false,
        };
      } else {
        // Explicit clear (type reset or 0 value) — remove any stale row.
        delete merged[id];
      }
    }
    return Object.values(merged);
  };

  const commit = (nextAudience, nextDrafts) => {
    const drf = nextDrafts || drafts;
    onChange({ audience: nextAudience, memberPrices: buildNextMp(drf) });
  };

  const toggleSelect = (id) => {
    const isCurrentlySelected = (audience || []).includes(id);
    const next = isCurrentlySelected ? audience.filter(a => a !== id) : [...(audience || []), id];
    // iter138: when un-selecting an audience, also drop that member_type's
    // draft discount so the persisted `member_prices` row is removed on the
    // next commit. Otherwise a stale discount survives — and the backend
    // (`_effective_price`) would keep applying it silently.
    let nextDrafts = drafts;
    if (isCurrentlySelected) {
      nextDrafts = { ...drafts, [id]: { type: "", value: 0 } };
      setDrafts(nextDrafts);
    }
    commit(next, nextDrafts);
  };

  const setType = (id, type) => {
    const cur = getDisc(id);
    const nd = { ...drafts, [id]: { type, value: type ? (cur.value || 0) : 0 } };
    setDrafts(nd);
    const isSel = (audience || []).includes(id);
    const nextAudience = type && !isSel ? [...(audience || []), id] : (audience || []);
    commit(nextAudience, nd);
  };

  const setValue = (id, value) => {
    const cur = getDisc(id);
    const nd = { ...drafts, [id]: { type: cur.type || "percent", value } };
    setDrafts(nd);
    commit(audience || [], nd);
  };

  return (
    <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
      <p className="text-xs font-semibold text-slate-700 mb-2">Audience & Pricing *</p>
      <p className="text-[11px] text-slate-500 mb-2">Select who can buy; leave discount empty to charge the base price.</p>
      <div className="space-y-2">
        {audienceRows.map(row => {
          const isSelected = (audience || []).includes(row.id);
          const disc = getDisc(row.id);
          const hasDiscountType = !!disc.type;
          const effective = hasDiscountType && disc.value ? _applyDisc(guestBase, disc) : guestBase;
          // Anonymous audience never gets a per-audience discount (they always pay base).
          const isAnonymous = row.id === "unregistered";
          return (
            <div key={row.id} className="flex items-center gap-2 flex-wrap" data-testid={`aud-row-${row.id}`}>
              <label className="flex items-center gap-2 flex-1 min-w-[130px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(row.id)}
                  className="accent-[#0ea5e9]"
                  data-testid={`aud-check-${row.id}`}
                />
                <span className="text-sm text-slate-700">{row.label}</span>
              </label>
              {isAnonymous ? (
                <span className="text-[11px] text-slate-400 italic px-2">Base price · no discount</span>
              ) : (
                <>
                  <select
                    value={disc.type || ""}
                    onChange={e => setType(row.id, e.target.value)}
                    className="px-2 py-1 rounded-lg border text-xs bg-white"
                    data-testid={`aud-type-${row.id}`}
                  >
                    <option value="">No discount</option>
                    <option value="percent">%</option>
                    <option value="fixed">€</option>
                  </select>
                  {hasDiscountType && (
                    <input
                      type="number" step="0.01" min="0"
                      value={disc.value ?? 0}
                      onChange={e => setValue(row.id, parseFloat(e.target.value) || 0)}
                      className="w-20 px-2 py-1 rounded-lg border text-xs bg-white"
                      data-testid={`aud-value-${row.id}`}
                    />
                  )}
                </>
              )}
              <span className={`text-xs font-mono font-bold ml-auto ${isSelected ? "text-[#2d8a4e]" : "text-slate-400"}`}>
                €{(isAnonymous ? guestBase : effective).toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------- Legacy alias (kept exported for any lingering imports) ----------
export const MemberPricesEditor = AudienceAndPricingEditor;

export default function ShopAdminDashboard() {
  const [tab, setTab] = useState("ticket-types");
  const { user, logout, authAxios } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();

  useEffect(() => { if (!user) navigate(`/${tenantSlug}/admin`); }, [user]);
  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shop-admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {tenant?.logo_url && <img src={tenant.logo_url} alt="" className="h-8 w-auto" />}
            <div>
              <p className="font-bold text-[#1e3a5f]">Shop Admin</p>
              <p className="text-xs text-slate-500">{user.name}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate(`/${tenantSlug}/admin`); }} className="p-2 hover:bg-slate-100 rounded-full" data-testid="logout-btn">
            <LogOut className="w-5 h-5 text-slate-600" />
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-4 flex gap-4 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-2 text-sm font-medium border-b-2 flex items-center gap-1.5 ${tab === t.id ? "border-[#0ea5e9] text-[#0ea5e9]" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">
        {tab === "ticket-types" && <TicketTypesTab authAxios={authAxios} />}
        {tab === "packages" && <PackagesTab authAxios={authAxios} />}
        {tab === "events" && <EventsTab authAxios={authAxios} />}
        {tab === "tickets" && <TicketsTab authAxios={authAxios} />}
        {tab === "issue" && <IssueTicketsTab authAxios={authAxios} />}
        {tab === "vouchers" && <VouchersTab authAxios={authAxios} />}
        {tab === "analytics" && <AnalyticsTab authAxios={authAxios} endpoint="/shop-admin/analytics" />}
        {tab === "settings" && <SettingsTab authAxios={authAxios} />}
      </main>
    </div>
  );
}

// ========== Settings ==========
function SettingsTab({ authAxios }) {
  const [s, setS] = useState({ banner_url: "", hero_title: "", hero_subtitle: "", cancellation_rules: [], cancellation_voucher_expiry_days: 0, cancellation_contact_email: "" });
  const [memberTypes, setMemberTypes] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [r, mt, tt] = await Promise.all([
      authAxios.get("/shop-admin/shop-settings"),
      authAxios.get("/ticketing/public/member-types").catch(() => ({ data: [] })),
      authAxios.get("/shop-admin/ticket-types").catch(() => ({ data: [] })),
    ]);
    setS({ banner_url: "", hero_title: "", hero_subtitle: "", cancellation_rules: [], cancellation_voucher_expiry_days: 0, cancellation_contact_email: "", ...r.data });
    setMemberTypes(mt.data || []);
    setTicketTypes(tt.data || []);
  };
  useEffect(() => { load(); }, []);

  const audienceOptions = [{ id: "guest", label: "Guest" }, { id: "member", label: "All Members" }, ...(memberTypes || []).map(m => ({ id: m.id, label: `Member: ${m.name}` })), { id: "*", label: "Any" }];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("File too large (max 4MB)");
    if (!f.type.startsWith("image/")) return toast.error("Only image files");
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const r = await authAxios.post("/shop-admin/upload-banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS(prev => ({ ...prev, banner_url: r.data.url }));
      toast.success("Banner uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const save = async () => {
    try { await authAxios.put("/shop-admin/shop-settings", s); toast.success("Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const updateRule = (i, k, v) => setS(prev => ({ ...prev, cancellation_rules: prev.cancellation_rules.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const toggleRuleTier = (i, ttid) => setS(prev => ({
    ...prev,
    cancellation_rules: prev.cancellation_rules.map((r, idx) => {
      if (idx !== i) return r;
      const cur = r.ticket_type_ids || [];
      return { ...r, ticket_type_ids: cur.includes(ttid) ? cur.filter(x => x !== ttid) : [...cur, ttid] };
    }),
  }));
  const addRule = () => setS(prev => ({ ...prev, cancellation_rules: [...prev.cancellation_rules, { audience: "guest", ticket_type_ids: [], cutoff_days: 1, refund_percent: 50, refund_destination: "wallet" }] }));
  const removeRule = (i) => setS(prev => ({ ...prev, cancellation_rules: prev.cancellation_rules.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-5" data-testid="shop-settings-tab">
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-bold text-[#1e3a5f] mb-3">Banner & Hero</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Banner image</label>
            <div className="flex items-center gap-3 mt-1">
              {s.banner_url && <img src={s.banner_url} alt="" className="h-16 w-32 object-cover rounded-lg" data-testid="banner-preview" />}
              <label className="flex-1 cursor-pointer">
                <input type="file" accept="image/*" onChange={upload} disabled={uploading} className="hidden" data-testid="banner-upload-input" />
                <div className={`px-3 py-2 rounded-xl border border-dashed text-sm text-center ${uploading ? "bg-slate-100 text-slate-400" : "bg-white text-[#1e3a5f] hover:bg-slate-50"}`}>{uploading ? "Uploading..." : (s.banner_url ? "Replace" : "Choose banner")}</div>
              </label>
              {s.banner_url && <button onClick={() => setS({ ...s, banner_url: "" })} className="text-xs text-red-500 underline">Remove</button>}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">PNG/JPG/WEBP, max 4MB. Recommended 1600x600.</p>
          </div>
          <div><label className="text-xs text-slate-500">Hero Title</label><input value={s.hero_title} onChange={e => setS({ ...s, hero_title: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="hero-title-input" /></div>
          <div><label className="text-xs text-slate-500">Hero Subtitle</label><input value={s.hero_subtitle} onChange={e => setS({ ...s, hero_subtitle: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="hero-subtitle-input" /></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1e3a5f]">Cancellation Rules</h3>
          <button onClick={addRule} className="text-sm text-[#0ea5e9] flex items-center gap-1" data-testid="add-cancellation-rule"><Plus className="w-4 h-4" /> Add Rule</button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Define cancellation windows + refund % per audience and (optionally) per tier. More specific rules win.</p>
        <div className="space-y-3">
          {(s.cancellation_rules || []).map((r, i) => (
            <div key={i} className="bg-slate-50 p-3 rounded-xl space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center">
                <select value={r.audience} onChange={e => updateRule(i, "audience", e.target.value)} className="px-2 py-1.5 rounded-lg border text-xs" data-testid={`rule-${i}-audience`}>
                  {audienceOptions.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                <div>
                  <label className="text-[10px] text-slate-500">Cutoff (days)</label>
                  <input type="number" min="0" value={r.cutoff_days} onChange={e => updateRule(i, "cutoff_days", parseInt(e.target.value) || 0)} className="w-full px-2 py-1.5 rounded-lg border text-xs" data-testid={`rule-${i}-cutoff`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">Refund %</label>
                  <input type="number" min="0" max="100" value={r.refund_percent} onChange={e => updateRule(i, "refund_percent", parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 rounded-lg border text-xs" data-testid={`rule-${i}-refund-pct`} />
                </div>
                <select value={r.refund_destination} onChange={e => updateRule(i, "refund_destination", e.target.value)} className="px-2 py-1.5 rounded-lg border text-xs" data-testid={`rule-${i}-dest`}>
                  <option value="wallet">→ Wallet</option>
                  <option value="voucher">→ Voucher</option>
                </select>
                <button onClick={() => removeRule(i)} className="text-red-400 justify-self-end p-1" data-testid={`rule-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Applies to tiers (leave empty = all tiers)</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ticketTypes.length === 0 && <span className="text-[10px] text-slate-400 italic">No ticket tiers yet</span>}
                  {ticketTypes.map(t => {
                    const active = (r.ticket_type_ids || []).includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleRuleTier(i, t.id)}
                        className={`px-2 py-1 rounded-full text-[10px] font-medium ${active ? "bg-[#0ea5e9] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
                        data-testid={`rule-${i}-tier-${t.id}`}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          {(!s.cancellation_rules || s.cancellation_rules.length === 0) && <p className="text-xs text-slate-400 italic">No rules — buyers cannot cancel.</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
          <div>
            <label className="text-xs text-slate-500">Cancellation Voucher Expiry (days)</label>
            <input
              type="number" min="0"
              value={s.cancellation_voucher_expiry_days || 0}
              onChange={e => setS({ ...s, cancellation_voucher_expiry_days: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 rounded-xl border text-sm"
              placeholder="0 = no expiry"
              data-testid="voucher-expiry-days-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Applies to vouchers issued from cancellations & FA refunds. 0 = no expiry.</p>
          </div>
          <div>
            <label className="text-xs text-slate-500">Cancellation Contact Email</label>
            <input
              type="email"
              value={s.cancellation_contact_email || ""}
              onChange={e => setS({ ...s, cancellation_contact_email: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border text-sm"
              placeholder="payments@your-domain.org"
              data-testid="cancellation-contact-email-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Shown on the ticket PDF for unregistered buyers who must email to cancel.</p>
          </div>
        </div>
      </div>

      <button onClick={save} className="w-full py-3 rounded-full bg-[#0ea5e9] text-white font-bold" data-testid="save-shop-settings">Save Settings</button>
    </div>
  );
}

// ========== Analytics ==========
// iter197: shared Shop-Admin download-xlsx button. Hits
// `/shop-admin/analytics/export` with the current scope so the Excel
// matches whatever is on screen. (FA has its own copy that routes to
// `/ticketing/fa/analytics/export`.)
function ShopDownloadAnalyticsBtn({ view, params, authAxios, testid }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const q = new URLSearchParams(params);
      q.set("view", view);
      const res = await authAxios.get(`/shop-admin/analytics/export?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analytics_${view}_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // No toast import in this file — leave silent; the test hook still
      // reaches the backend call. UI shows the retry via the disabled flip.
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
      title="Download this table as Excel"
    >
      <Download className="w-3 h-3" />
      <span>Excel</span>
    </button>
  );
}

function AnalyticsTab({ authAxios, endpoint }) {
  const [days, setDays] = useState(30);
  const [customMode, setCustomMode] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    let url = `${endpoint}?days=${days}`;
    if (customMode && startDate && endDate) {
      url = `${endpoint}?start_date=${startDate}&end_date=${endDate}`;
    }
    authAxios.get(url).then(r => setData(r.data)).catch(() => setData(null));
  }, [days, endpoint, customMode, startDate, endDate]);

  if (!data) return <p className="text-slate-400 text-center py-8">Loading analytics...</p>;
  const k = data.kpis;
  const analyticsParams = (() => {
    const p = new URLSearchParams();
    if (customMode && startDate && endDate) {
      p.set("start_date", startDate); p.set("end_date", endDate);
    } else {
      p.set("days", String(days));
    }
    return p.toString();
  })();

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      <div className="flex flex-wrap gap-2 items-center">
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => { setCustomMode(false); setDays(d); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${!customMode && days === d ? "bg-[#0ea5e9] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            data-testid={`analytics-preset-${d}d`}
          >{d}d</button>
        ))}
        <button
          onClick={() => setCustomMode(v => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${customMode ? "bg-[#0ea5e9] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
          data-testid="analytics-custom-toggle"
        >Custom range</button>
        {customMode && (
          <div className="flex items-center gap-2 text-xs">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200" data-testid="analytics-start-date" />
            <span className="text-slate-400">→</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200" data-testid="analytics-end-date" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="analytics-kpi-row">
        <Kpi label="Tickets" value={k.tickets_total} />
        <Kpi label="Paid" value={k.tickets_paid} color="green" />
        <Kpi label="Used" value={k.tickets_used} color="slate" />
        <Kpi label="Refunded" value={k.tickets_refunded} color="red" />
        <Kpi label="Cancelled" value={k.tickets_cancelled || 0} color="amber" />
        <Kpi label="Revenue" value={`€${k.gross_revenue.toFixed(2)}`} color="blue" />
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-bold text-[#1e3a5f] mb-3">Daily revenue</h3>
        <DailyBars rows={data.by_day} />
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1e3a5f]">Top events</h3>
          <ShopDownloadAnalyticsBtn view="by_event" params={analyticsParams} authAxios={authAxios} testid="analytics-download-by-event" />
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Event</th><th className="text-right">Count</th><th className="text-right">Revenue</th></tr></thead>
          <tbody>
            {data.by_event.slice(0, 10).map(e => (
              <tr key={e.event_id} className="border-t border-slate-100">
                <td className="py-2">{e.event_name}</td>
                <td className="text-right">{e.count}</td>
                <td className="text-right font-bold">€{e.revenue.toFixed(2)}</td>
              </tr>
            ))}
            {data.by_event.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-slate-400">No data</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-[#1e3a5f]">By tier</h3>
          <ShopDownloadAnalyticsBtn view="by_tier" params={analyticsParams} authAxios={authAxios} testid="analytics-download-by-tier" />
        </div>
        <p className="text-[10px] text-slate-400 mb-2">Package sales are broken down into their component tiers (with &quot;from package&quot; indicator). Only valid tickets counted (void, cancelled and refunded excluded).</p>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Tier</th><th className="text-right">Count</th><th className="text-right">From package</th><th className="text-right">% of capacity</th><th className="text-right">Revenue</th></tr></thead>
          <tbody>
            {data.by_tier.map(t => (
              <tr key={t.tier} className="border-t border-slate-100" data-testid={`analytics-tier-row-${t.tier}`}>
                <td className="py-2">{t.tier}</td>
                <td className="text-right">{t.count}</td>
                <td className="text-right text-slate-500">{t.from_package_count || 0}</td>
                <td className="text-right" data-testid={`analytics-tier-cap-${t.tier}`}>
                  {t.capacity_pct == null
                    ? <span className="text-slate-400" title="Unbounded capacity">—</span>
                    : <span className={t.capacity_pct >= 90 ? "text-red-600 font-semibold" : t.capacity_pct >= 70 ? "text-amber-600" : "text-slate-600"}>{t.capacity_pct}% <span className="text-[10px] text-slate-400">({t.count}/{t.capacity})</span></span>
                  }
                </td>
                <td className="text-right font-bold">€{t.revenue.toFixed(2)}</td>
              </tr>
            ))}
            {data.by_tier.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No data</td></tr>}
          </tbody>
        </table>
      </div>
      <ByTierSplitTable rows={data.by_tier_split || []} download={<ShopDownloadAnalyticsBtn view="by_tier_split" params={analyticsParams} authAxios={authAxios} testid="analytics-download-by-tier-split" />} testidPrefix="analytics" />
      {data.by_package && data.by_package.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="analytics-by-package">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#1e3a5f]">By package</h3>
            <ShopDownloadAnalyticsBtn view="by_package" params={analyticsParams} authAxios={authAxios} testid="analytics-download-by-package" />
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Package</th><th className="text-right">Packages sold</th><th className="text-right">Ticket rows</th><th className="text-right">Revenue</th></tr></thead>
            <tbody>
              {data.by_package.map(p => (
                <tr key={p.package_id} className="border-t border-slate-100">
                  <td className="py-2">{p.name}</td>
                  <td className="text-right">{p.packages_sold}</td>
                  <td className="text-right text-slate-500">{p.ticket_rows}</td>
                  <td className="text-right font-bold">€{p.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ByVariantTable rows={data.by_variant || []} download={<ShopDownloadAnalyticsBtn view="by_variant" params={analyticsParams} authAxios={authAxios} testid="analytics-download-by-variant" />} />
      <ByVariantSplitTable rows={data.by_variant_split || []} download={<ShopDownloadAnalyticsBtn view="by_variant_split" params={analyticsParams} authAxios={authAxios} testid="analytics-download-by-variant-split" />} testidPrefix="analytics" />
    </div>
  );
}

// iter166: Split "By tier" table — member vs guest counts side by side,
// with an expandable member-type breakdown for member sales so admins can
// see "of 40 VS tickets, 25 Family Adult / 10 Child / 5 Singles" at a glance.
function ByTierSplitTable({ rows, testidPrefix = "analytics", download }) {
  const [expanded, setExpanded] = useState(() => new Set());
  if (!rows || rows.length === 0) return null;
  const toggle = (tier) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(tier)) next.delete(tier); else next.add(tier);
    return next;
  });
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`${testidPrefix}-by-tier-split`}>
      <div className="flex items-center justify-between mb-1"><h3 className="font-bold text-[#1e3a5f]">By tier — member vs guest</h3>{download}</div>
      <p className="text-[10px] text-slate-400 mb-3">Click a member count to see the member-type split.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400">
            <th className="py-1">Tier</th>
            <th className="text-right">Members</th>
            <th className="text-right">Member €</th>
            <th className="text-right">Guests</th>
            <th className="text-right">Guest €</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const isOpen = expanded.has(r.tier);
            const hasMemberSplit = (r.by_member_type || []).length > 0;
            return (
              <React.Fragment key={r.tier}>
                <tr className="border-t border-slate-100" data-testid={`${testidPrefix}-tier-split-row-${r.tier}`}>
                  <td className="py-2">{r.tier}</td>
                  <td className="text-right">
                    {hasMemberSplit ? (
                      <button onClick={() => toggle(r.tier)} className="text-[#0ea5e9] hover:underline" data-testid={`${testidPrefix}-tier-split-expand-${r.tier}`}>
                        {r.member_count} {isOpen ? "▾" : "▸"}
                      </button>
                    ) : r.member_count}
                  </td>
                  <td className="text-right font-bold">€{r.member_revenue.toFixed(2)}</td>
                  <td className="text-right text-slate-500">{r.guest_count}</td>
                  <td className="text-right text-slate-500">€{r.guest_revenue.toFixed(2)}</td>
                </tr>
                {isOpen && (r.by_member_type || []).map(mt => (
                  <tr key={`${r.tier}-${mt.member_type}`} className="bg-slate-50" data-testid={`${testidPrefix}-tier-split-mtype-${r.tier}-${mt.member_type}`}>
                    <td className="py-1 pl-6 text-xs text-slate-500">↳ {mt.member_type}</td>
                    <td className="text-right text-xs">{mt.count}</td>
                    <td className="text-right text-xs">€{mt.revenue.toFixed(2)}</td>
                    <td colSpan={2} className="text-xs text-slate-400 pl-4">member-only</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// iter166: Split "By variant" table grouped by event. Same shape as
// ByTierSplitTable but with the variant column and refunded counters.
function ByVariantSplitTable({ rows, testidPrefix = "analytics", download }) {
  const [expanded, setExpanded] = useState(() => new Set());
  if (!rows || rows.length === 0) return null;
  const grouped = rows.reduce((acc, r) => {
    const k = r.event_id || "";
    if (!acc[k]) acc[k] = { name: r.event_name, list: [] };
    acc[k].list.push(r);
    return acc;
  }, {});
  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`${testidPrefix}-by-variant-split`}>
      <div className="flex items-center justify-between mb-1"><h3 className="font-bold text-[#1e3a5f]">By tier variant — member vs guest</h3>{download}</div>
      <p className="text-[10px] text-slate-400 mb-3">Grouped by event. Member counts expand into the member-type split.</p>
      {Object.entries(grouped).map(([eid, grp]) => (
        <div key={eid} className="mb-4 last:mb-0">
          <p className="text-xs font-bold text-slate-600 mb-1">{grp.name || "(unknown event)"}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="py-1">Tier</th>
                <th>Variant</th>
                <th className="text-right">Members</th>
                <th className="text-right">Member €</th>
                <th className="text-right">Guests</th>
                <th className="text-right">Guest €</th>
              </tr>
            </thead>
            <tbody>
              {grp.list.map((r, idx) => {
                const rowKey = `${eid}-${r.tier}-${r.variant}-${idx}`;
                const isOpen = expanded.has(rowKey);
                const hasMemberSplit = (r.by_member_type || []).length > 0;
                return (
                  <>
                    <tr key={rowKey} className="border-t border-slate-100">
                      <td className="py-2">{r.tier}</td>
                      <td className="text-slate-500">{r.variant || "—"}</td>
                      <td className="text-right">
                        {hasMemberSplit ? (
                          <button onClick={() => toggle(rowKey)} className="text-[#0ea5e9] hover:underline">
                            {r.member_count} {isOpen ? "▾" : "▸"}
                          </button>
                        ) : r.member_count}
                        
                      </td>
                      <td className="text-right font-bold">€{Number(r.member_revenue || 0).toFixed(2)}</td>
                      <td className="text-right text-slate-500">
                        {r.guest_count}
                        
                      </td>
                      <td className="text-right text-slate-500">€{Number(r.guest_revenue || 0).toFixed(2)}</td>
                    </tr>
                    {isOpen && (r.by_member_type || []).map(mt => (
                      <tr key={`${rowKey}-${mt.member_type}`} className="bg-slate-50">
                        <td colSpan={2} className="py-1 pl-6 text-xs text-slate-500">↳ {mt.member_type}</td>
                        <td className="text-right text-xs">{mt.count}</td>
                        <td className="text-right text-xs">€{Number(mt.revenue || 0).toFixed(2)}</td>
                        <td colSpan={2} className="text-xs text-slate-400 pl-4">member-only</td>
                      </tr>
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// Per-event tier→variant breakdown. Grouped by event so multi-event ranges
// stay readable; each event heading shows the total row count for context.
function ByVariantTable({ rows, download }) {
  if (!rows || rows.length === 0) return null;
  const grouped = rows.reduce((acc, r) => {
    const key = r.event_id || "";
    if (!acc[key]) acc[key] = { name: r.event_name, list: [] };
    acc[key].list.push(r);
    return acc;
  }, {});
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="analytics-by-variant">
      <div className="flex items-center justify-between mb-1"><h3 className="font-bold text-[#1e3a5f]">By tier variant</h3>{download}</div>
      <p className="text-[10px] text-slate-400 mb-3">Valid tickets sold and net revenue per variant, grouped by event (refunded, cancelled and void excluded).</p>
      {Object.entries(grouped).map(([eid, grp]) => (
        <div key={eid} className="mb-4 last:mb-0" data-testid={`variant-event-${eid}`}>
          <p className="text-xs font-bold text-slate-600 mb-1">{grp.name || "(unknown event)"}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="py-1">Tier</th>
                <th>Variant</th>
                <th className="text-right">Sold</th>
                <th className="text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {grp.list.map((r, idx) => (
                <tr key={`${r.tier}-${r.variant}-${idx}`} className="border-t border-slate-100">
                  <td className="py-2">{r.tier}</td>
                  <td className="text-slate-500">{r.variant || "—"}</td>
                  <td className="text-right">{r.count}</td>
                  <td className="text-right font-bold">€{Number(r.revenue || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const Kpi = ({ label, value, color = "slate" }) => {
  const colors = { slate: "bg-slate-50 text-slate-700", green: "bg-green-50 text-green-700", red: "bg-red-50 text-red-700", blue: "bg-blue-50 text-blue-700", amber: "bg-amber-50 text-amber-700" };
  return (
    <div className={`p-4 rounded-2xl ${colors[color]}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-60">{label}</p>
      <p className="text-2xl font-extrabold mt-1">{value}</p>
    </div>
  );
};

const DailyBars = ({ rows }) => {
  if (!rows || rows.length === 0) return <p className="text-xs text-slate-400 py-4">No data</p>;
  const max = Math.max(...rows.map(r => r.revenue), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {rows.map(r => (
        <div key={r.date} className="flex-1 flex flex-col items-center gap-1" title={`${r.date}: €${r.revenue.toFixed(2)}`}>
          <div className="w-full bg-[#0ea5e9] rounded-t" style={{ height: `${(r.revenue / max) * 100}%` }} />
          <span className="text-[8px] text-slate-400">{r.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

// ========== Ticket Types ==========
function TicketTypesTab({ authAxios }) {
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ event_id: "", name: "", description: "", banner_url: "", audience: [], price: 0, capacity: 0, sales_start: "", sales_end: "", valid_dates: [], is_active: true });

  const fetchAll = async () => {
    const [ev, tt, mt] = await Promise.all([
      authAxios.get("/shop-admin/events"),
      authAxios.get("/shop-admin/ticket-types" + (selectedEvent ? `?event_id=${selectedEvent}` : "")),
      authAxios.get("/ticketing/public/member-types").catch(() => ({ data: [] })),
    ]);
    setEvents(ev.data); setTypes(tt.data); setMemberTypes(mt.data || []);
  };
  useEffect(() => { fetchAll(); }, [selectedEvent]);

  const audienceOptions = [{ id: "guest", label: "Guest" }, { id: "member", label: "All Members" }, ...(memberTypes || []).map(m => ({ id: m.id, label: `Member: ${m.name}` }))];

  const openNew = () => {
    setEditing(null);
    // Default audience: Unregistered + Guest + all currently-defined member types
    // so a freshly-created tier is available to everyone unless the admin narrows it.
    const defaultAudience = ["unregistered", "guest", ...(memberTypes || []).map(m => m.id)];
    setForm({ event_id: selectedEvent || (events[0]?.id || ""), name: "", description: "", banner_url: "", audience: defaultAudience, price: 0, capacity: 0, sales_start: "", sales_end: "", valid_dates: [], is_active: true, variants: [], discount: null, member_prices: [], sort_order: 0, category: "", category_sort_order: 0 });
    setShowForm(true);
  };
  const toggleActive = async (t) => {
    try {
      await authAxios.put(`/shop-admin/ticket-types/${t.id}`, { is_active: !t.is_active });
      toast.success(`${t.name} ${!t.is_active ? "activated" : "deactivated"}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const openEdit = (t) => {
    const rawAud = t.audience || [];
    // Legacy "member" (=all members) doesn't have a row in AudienceAndPricingEditor.
    // Expand it into all currently-known member type ids so the checkboxes reflect
    // reality and the user can uncheck them.
    let audience = rawAud.includes("member")
      ? Array.from(new Set([
          ...rawAud.filter(a => a !== "member"),
          ...(memberTypes || []).map(m => m.id),
        ]))
      : rawAud;
    // Legacy tiers (audience_v < 2) implicitly include "unregistered" — surface that
    // in the UI so the admin sees the same state the public shop is respecting.
    if ((t.audience_v || 1) < 2 && !audience.includes("unregistered")) {
      audience = ["unregistered", ...audience];
    }
    setEditing(t);
    setForm({ ...t, audience, variants: t.variants || [], discount: t.discount || null, member_prices: t.member_prices || [], valid_dates: t.valid_dates || [] });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.event_id || !form.name || !form.audience?.length) return toast.error("Event, name and audience required");
    try {
      if (editing) await authAxios.put(`/shop-admin/ticket-types/${editing.id}`, form);
      else await authAxios.post("/shop-admin/ticket-types", form);
      toast.success("Saved");
      setShowForm(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    try { await authAxios.delete(`/shop-admin/ticket-types/${id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const duplicate = async (t) => {
    try {
      const r = await authAxios.post(`/shop-admin/ticket-types/${t.id}/duplicate`);
      toast.success(`Duplicated: ${r.data.name}`);
      fetchAll();
      openEdit(r.data);  // Open the copy immediately so the admin can adjust it
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };
  const toggleAudience = (id) => setForm(f => ({ ...f, audience: f.audience.includes(id) ? f.audience.filter(a => a !== id) : [...f.audience, id] }));

  // Reorder by swapping sort_order values with the adjacent card in the
  // SAME category. Handles ties (both at same sort_order) by nudging one
  // value up/down. Called from arrow buttons inside a category group.
  const moveTier = async (groupItems, idx, direction) => {
    const other = direction === "up" ? idx - 1 : idx + 1;
    if (other < 0 || other >= groupItems.length) return;
    const a = groupItems[idx];
    const b = groupItems[other];
    const aOrder = Number.isFinite(a.sort_order) ? a.sort_order : 0;
    const bOrder = Number.isFinite(b.sort_order) ? b.sort_order : 0;
    let newA, newB;
    if (aOrder !== bOrder) {
      newA = bOrder; newB = aOrder;
    } else {
      newA = direction === "up" ? aOrder - 1 : aOrder + 1;
      newB = bOrder;
    }
    try {
      await Promise.all([
        authAxios.put(`/shop-admin/ticket-types/${a.id}`, { sort_order: newA }),
        authAxios.put(`/shop-admin/ticket-types/${b.id}`, { sort_order: newB }),
      ]);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Reorder failed"); }
  };

  const addVariant = () => setForm(f => ({ ...f, variants: [...(f.variants || []), { id: `v-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "", price_override: null }] }));
  const updateVariant = (idx, patch) => setForm(f => ({ ...f, variants: (f.variants || []).map((v, i) => i === idx ? { ...v, ...patch } : v) }));
  const removeVariant = (idx) => setForm(f => ({ ...f, variants: (f.variants || []).filter((_, i) => i !== idx) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)} className="px-3 py-2 rounded-xl border text-sm" data-testid="event-filter">
          <option value="">All events</option>
          {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <button onClick={openNew} className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#0ea5e9] text-white text-sm font-medium" data-testid="add-ticket-type-btn">
          <Plus className="w-4 h-4" /> New Ticket Tier
        </button>
      </div>
      <div className="space-y-5">
        {groupByCategory(types).map(group => (
          <section key={group.key} data-testid={`tier-group-${group.key}`}>
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 px-1" data-testid={`tier-group-label-${group.key}`}>
              {group.label}
              <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case">{group.items.length} item{group.items.length !== 1 ? "s" : ""}</span>
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {group.items.map((t, idx) => (
          <div key={t.id} className="bg-white rounded-2xl p-4 shadow-sm relative">
            <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-slate-50 rounded-full border border-slate-200 px-1 py-0.5 shadow-sm" data-testid={`ticket-reorder-${t.id}`}>
              <button
                onClick={() => moveTier(group.items, idx, "up")}
                disabled={idx === 0}
                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 hover:text-[#0ea5e9] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up within category"
                data-testid={`ticket-move-up-${t.id}`}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => moveTier(group.items, idx, "down")}
                disabled={idx === group.items.length - 1}
                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 hover:text-[#0ea5e9] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down within category"
                data-testid={`ticket-move-down-${t.id}`}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex justify-between items-start pt-6">
              <div>
                <h4 className="font-bold text-[#1e3a5f]">{t.name}</h4>
                <p className="text-xs text-slate-500 mt-1">{events.find(e => e.id === t.event_id)?.name || t.event_id}</p>
                <p className="text-lg font-extrabold text-[#2d8a4e] mt-2">€{t.price?.toFixed(2)}</p>
                <p className="text-xs text-slate-400 mt-1" data-testid={`tier-sold-${t.id}`}>Sold: {t.sold || 0} / {t.capacity || "∞"}{t.held ? <span className="text-amber-600"> · {t.held} on hold</span> : null}{t.seats_left != null ? <span> · {t.seats_left} left</span> : null}</p>
                {t.walkin_allowed && <p className="text-xs text-amber-700 mt-0.5" data-testid={`ticket-walkin-${t.id}`}>Walk-in: {t.walkin_sold || 0} / {t.walkin_capacity}</p>}
                <div className="flex flex-wrap gap-1 mt-2" data-testid={`ticket-audience-${t.id}`}>
                  {(() => {
                    const aud = t.audience || [];
                    const memberTypeIds = new Set((memberTypes || []).map(m => m.id));
                    const showUnreg = aud.includes("unregistered") || (t.audience_v || 1) < 2;
                    const showGuest = aud.includes("guest");
                    const showMember = aud.includes("member") || aud.some(a => memberTypeIds.has(a));
                    const pills = [];
                    if (showUnreg) pills.push({ key: "unregistered", label: "Unregistered", cls: "bg-slate-200 text-slate-700" });
                    if (showGuest) pills.push({ key: "guest", label: "Guest", cls: "bg-amber-100 text-amber-700" });
                    if (showMember) pills.push({ key: "member", label: "Member", cls: "bg-blue-100 text-blue-700" });
                    return pills.map(p => (
                      <span key={p.key} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${p.cls}`} data-testid={`ticket-audience-pill-${t.id}-${p.key}`}>{p.label}</span>
                    ));
                  })()}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={() => toggleActive(t)}
                  className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full transition-colors ${t.is_active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}
                  data-testid={`ticket-active-toggle-${t.id}`}
                  title={t.is_active ? "Click to deactivate" : "Click to activate"}
                >
                  <span className={`w-2 h-2 rounded-full ${t.is_active ? "bg-green-500" : "bg-slate-400"}`} />
                  {t.is_active ? "Active" : "Inactive"}
                </button>
                <button onClick={() => openEdit(t)} className="text-blue-500" data-testid={`ticket-edit-${t.id}`}><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => duplicate(t)} className="text-slate-500 hover:text-[#0ea5e9]" title="Duplicate this tier" data-testid={`ticket-duplicate-${t.id}`}><Copy className="w-4 h-4" /></button>
                <button onClick={() => remove(t.id)} className="text-red-400" data-testid={`ticket-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {showForm && (
        <Modal title={editing ? "Edit Ticket Tier" : "New Ticket Tier"} onClose={() => setShowForm(false)}>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Event *</label>
              <select value={form.event_id} onChange={e => setForm({ ...form, event_id: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" disabled={!!editing}>
                {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-slate-500">Tier Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" placeholder="e.g. General Admission" /></div>
            <div><label className="text-xs text-slate-500">Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" rows={2} /></div>
            <BannerUpload authAxios={authAxios} value={form.banner_url} onChange={url => setForm({ ...form, banner_url: url })} purpose="tier-banner" label="Tier Banner (optional)" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Price (€) *</label><input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
              <div><label className="text-xs text-slate-500">Capacity (0 = ∞)</label><input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
            </div>
            <div className="rounded-xl border p-3 bg-slate-50" data-testid="walkin-settings">
              <label className="flex items-center gap-2 text-sm font-medium text-[#1e3a5f] cursor-pointer">
                <input type="checkbox" checked={!!form.walkin_allowed} onChange={e => setForm({ ...form, walkin_allowed: e.target.checked, walkin_capacity: e.target.checked ? (form.walkin_capacity || 1) : 0 })} data-testid="walkin-allowed-toggle" />
                Allow walk-in sales at the event desk
              </label>
              {form.walkin_allowed && (
                <div className="mt-2">
                  <label className="text-xs text-slate-500">Walk-in capacity * (extra seats on top of online capacity)</label>
                  <input type="number" min="1" value={form.walkin_capacity || ""} onChange={e => setForm({ ...form, walkin_capacity: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-xl border text-sm bg-white" data-testid="walkin-capacity-input" />
                </div>
              )}
            </div>
            <DiscountEditor value={form.discount} onChange={d => setForm({ ...form, discount: d })} />
            <AudienceAndPricingEditor
              audience={form.audience || []}
              memberPrices={form.member_prices || []}
              memberTypes={memberTypes}
              basePrice={form.price}
              baseDiscount={form.discount}
              onChange={({ audience, memberPrices }) => setForm({ ...form, audience, member_prices: memberPrices })}
            />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-500">Variants (e.g. veg / non-veg) — inherits tier price/capacity by default</label>
                <button type="button" onClick={addVariant} className="text-xs text-[#0ea5e9] hover:underline" data-testid="add-variant-btn">+ Add variant</button>
              </div>
              <div className="space-y-1.5">
                {(form.variants || []).map((v, idx) => (
                  <div key={v.id} className="flex gap-2 items-center">
                    <input
                      value={v.name}
                      onChange={e => updateVariant(idx, { name: e.target.value })}
                      className="flex-1 px-2 py-1.5 rounded-lg border text-sm"
                      placeholder="Variant name"
                      data-testid={`variant-name-${idx}`}
                    />
                    <input
                      type="number" step="0.01"
                      value={v.price_override ?? ""}
                      onChange={e => updateVariant(idx, { price_override: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      className="w-28 px-2 py-1.5 rounded-lg border text-sm"
                      placeholder="Override €"
                    />
                    <button type="button" onClick={() => removeVariant(idx)} className="text-red-400 p-1"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Sales Start</label><input type="datetime-local" value={form.sales_start || ""} onChange={e => setForm({ ...form, sales_start: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
              <div><label className="text-xs text-slate-500">Sales End</label><input type="datetime-local" value={form.sales_end || ""} onChange={e => setForm({ ...form, sales_end: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
            </div>
            {/* iter160: display order controls how tiers/packages are stacked
                on the public shop card. Lower number = shown first. Leave
                at 0 for the default (creation-order) sort. */}
            <div>
              <label className="text-xs text-slate-500">Display order (lower = shown first)</label>
              <input
                type="number"
                value={form.sort_order ?? 0}
                onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
                className="w-full px-3 py-2 rounded-xl border text-sm"
                placeholder="0"
                data-testid="tier-sort-order-input"
              />
            </div>
            {/* iter162: category grouping — tiers sharing the same trimmed
                label render under a single header on both admin & public
                shop. Empty = "Other" bucket at the bottom. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Category (optional)</label>
                <input
                  type="text"
                  value={form.category || ""}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  placeholder="e.g. Weekend Pass"
                  data-testid="tier-category-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Category order</label>
                <input
                  type="number"
                  value={form.category_sort_order ?? 0}
                  onChange={e => setForm({ ...form, category_sort_order: parseInt(e.target.value || "0", 10) })}
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  placeholder="0"
                  data-testid="tier-category-sort-order-input"
                />
              </div>
            </div>
            <ValidDatesPicker
              event={events.find(ev => ev.id === form.event_id)}
              value={form.valid_dates || []}
              onChange={dates => setForm({ ...form, valid_dates: dates })}
            />
            <OnAttendCreditFields
              form={form}
              setForm={setForm}
            />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            <button onClick={save} className="w-full py-3 rounded-full bg-[#0ea5e9] text-white font-bold" data-testid="save-ticket-type">{editing ? "Update" : "Create"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// On-attend credit config: amount + optional PDF note. Only expanded when
// amount > 0. Credit validity is always tied to the ticket's own valid_dates
// (per the rule: credit day = ticket day) — no separate picker needed.
function OnAttendCreditFields({ form, setForm }) {
  const enabled = (parseFloat(form.on_attend_credit_amount) || 0) > 0;
  return (
    <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/60" data-testid="on-attend-credit-block">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#1e3a5f]">On-attend credit (optional)</p>
          <p className="text-[10px] text-slate-500">Refund part of the ticket price into the buyer&apos;s wallet (or a coupon by email for unregistered buyers) when they check in. Spendable only at merchant stalls at this event.</p>
        </div>
        <input
          type="number" step="0.01" min="0"
          value={form.on_attend_credit_amount || 0}
          onChange={e => setForm({ ...form, on_attend_credit_amount: parseFloat(e.target.value) || 0 })}
          className="w-24 px-2 py-1.5 rounded-lg border text-sm text-right"
          placeholder="€"
          data-testid="on-attend-credit-amount"
        />
      </div>
      {enabled && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] text-slate-400">The credit is spendable only on the days the ticket is valid (its &ldquo;Valid on&rdquo; dates, or the full event window if none set).</p>
          <div>
            <label className="text-xs text-slate-500">PDF note (optional)</label>
            <input
              value={form.on_attend_credit_note || ""}
              onChange={e => setForm({ ...form, on_attend_credit_note: e.target.value })}
              placeholder="e.g. €10 food & drink credit"
              className="w-full px-3 py-2 rounded-xl border text-sm"
              maxLength={200}
              data-testid="on-attend-credit-note"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Per-day validity picker for multi-day events. Empty selection = "valid all
// event days" (multi-day pass). For single-day events we don't render anything
// since there's only one option.
function ValidDatesPicker({ event, value, onChange }) {
  const days = React.useMemo(() => _dayRange(event?.start_date, event?.end_date), [event?.start_date, event?.end_date]);
  if (!event || days.length <= 1) return null;
  const inRange = new Set(days);
  const stale = (value || []).filter(d => !inRange.has(d));
  const selected = new Set((value || []).filter(d => inRange.has(d)));
  const toggle = (d) => {
    const next = new Set(selected);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    onChange(Array.from(next).sort());
  };
  return (
    <div data-testid="valid-dates-picker">
      <label className="text-xs text-slate-500">Valid on which days?</label>
      <p className="text-[10px] text-slate-400 mb-1">Leave all unchecked = multi-day pass (any event day).</p>
      {stale.length > 0 && (
        <div className="mb-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="valid-dates-stale-warning">
          This ticket is still set to {stale.length} day(s) outside the event dates ({stale.map(_prettyDay).join(", ")}), so it can't be sold at the door or scanned during the event.
          Pick the right days below, or{" "}
          <button type="button" onClick={() => onChange(Array.from(selected).sort())} className="underline font-semibold" data-testid="valid-dates-clear-stale">remove the old days</button>.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {days.map(d => (
          <button
            type="button"
            key={d}
            onClick={() => toggle(d)}
            className={`px-3 py-1.5 rounded-full text-xs border ${selected.has(d) ? "bg-[#1e3a5f] text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`valid-date-${d}`}
          >
            {_prettyDay(d)}
          </button>
        ))}
      </div>
    </div>
  );
}

// Enumerate YYYY-MM-DD days between event.start_date and event.end_date inclusive.
// iter158: parse strictly in UTC and iterate with setUTCDate so the resulting
// ISO strings match the calendar dates the shop admin typed in. Previously
// `new Date(startISO)` parsed "2026-10-16" as UTC-midnight, then setHours
// (local midnight) rewound the underlying UTC to the previous day in any
// positive-offset TZ (Berlin UTC+2 → "2026-10-15"), and toISOString() then
// emitted that wrong ISO. Prod symptom: Durgapuja 2026 (16-18 Oct) showed
// Thu 15/Fri 16/Sat 17 in the "Valid on which days?" picker.
function _dayRange(startISO, endISO) {
  if (!startISO || !endISO) return [];
  const s10 = String(startISO).slice(0, 10);
  const e10 = String(endISO).slice(0, 10);
  const start = new Date(s10 + "T00:00:00Z");
  const end = new Date(e10 + "T00:00:00Z");
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];
  const out = [];
  const cur = new Date(start.getTime());
  while (cur.getTime() <= end.getTime()) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

function _prettyDay(iso) {
  try {
    // iter158: parse as UTC and format with timeZone: "UTC" so the label
    // ("Fri 16 Oct") always matches the ISO the picker stored — otherwise
    // a browser west of the source TZ could shift the displayed day.
    const d = new Date(String(iso).slice(0, 10) + "T00:00:00Z");
    return d.toLocaleDateString(undefined, {
      weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
    });
  } catch (e) {
    return iso;
  }
}

// ========== Events (marketing edit) ==========
function EventsTab({ authAxios }) {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { authAxios.get("/shop-admin/events").then(r => setEvents(r.data)); }, []);
  const openEdit = (e) => { setEditing(e); setForm({ marketing_description: e.marketing_description || "", marketing_banner_url: e.marketing_banner_url || "", is_shop_active: e.is_shop_active !== false }); };
  const save = async () => {
    try {
      await authAxios.put(`/shop-admin/events/${editing.id}/marketing`, form);
      toast.success("Saved");
      setEditing(null);
      authAxios.get("/shop-admin/events").then(r => setEvents(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-3">
      {events.map(e => (
        <div key={e.id} className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between gap-4">
          <div className="flex-1">
            <h4 className="font-bold text-[#1e3a5f]">{e.name}</h4>
            <p className="text-xs text-slate-500 mt-1">{e.start_date}{e.end_date && e.end_date !== e.start_date ? ` – ${e.end_date}` : ""} · {e.location}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${e.is_shop_active !== false ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"}`}>{e.is_shop_active !== false ? "In Shop" : "Hidden"}</span>
            </div>
          </div>
          <button onClick={() => openEdit(e)} className="text-blue-500 p-2" data-testid={`edit-event-marketing-${e.id}`}><Edit2 className="w-4 h-4" /></button>
        </div>
      ))}
      {editing && (
        <Modal title="Edit Event Marketing" onClose={() => setEditing(null)}>
          <div className="space-y-3">
            {/* Read-only master data from FA */}
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Event details (read-only)</p>
              <p className="text-sm font-bold text-[#1e3a5f]">{editing.name}</p>
              <p className="text-xs text-slate-600">
                {editing.start_date}{editing.end_date && editing.end_date !== editing.start_date ? ` – ${editing.end_date}` : ""}
                {editing.location ? ` · ${editing.location}` : ""}
              </p>
            </div>
            <BannerUpload authAxios={authAxios} value={form.marketing_banner_url} onChange={url => setForm({ ...form, marketing_banner_url: url })} purpose="event-banner" label="Event Banner (optional)" />
            <div><label className="text-xs text-slate-500">Description</label><textarea value={form.marketing_description} onChange={e => setForm({ ...form, marketing_description: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_shop_active} onChange={e => setForm({ ...form, is_shop_active: e.target.checked })} /> Visible in public shop</label>
            <button onClick={save} className="w-full py-3 rounded-full bg-[#0ea5e9] text-white font-bold">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========== Tickets ==========
function TicketsTab({ authAxios }) {
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [collapsedEvents, setCollapsedEvents] = useState({});
  const [exportingId, setExportingId] = useState(null);
  const load = () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    if (search.trim()) params.set("search", search.trim());
    const q = params.toString() ? `?${params.toString()}` : "";
    Promise.all([
      authAxios.get(`/shop-admin/tickets${q}`),
      authAxios.get("/shop-admin/events"),
    ]).then(([tt, ee]) => { setTickets(tt.data); setEvents(ee.data); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const exportEvent = async (eid, eventName) => {
    setExportingId(eid);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (search.trim()) params.set("search", search.trim());
      const q = params.toString() ? `?${params.toString()}` : "";
      const r = await authAxios.get(`/ticketing/shop-admin/events/${eid}/tickets/export${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (eventName || "event").replace(/[^\w-]+/g, "_").slice(0, 40);
      a.download = `tickets_${safeName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExportingId(null);
    }
  };

  // Group by event
  const eventMap = Object.fromEntries(events.map(e => [e.id, e]));
  const grouped = tickets.reduce((acc, t) => {
    const key = t.event_id || "unknown";
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});
  const groupKeys = Object.keys(grouped).sort((a, b) => {
    const na = eventMap[a]?.name || "z";
    const nb = eventMap[b]?.name || "z";
    return na.localeCompare(nb);
  });

  return (
    <div>
      <PendingLinksPanel authAxios={authAxios} />
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        {["", "paid", "used", "refunded"].map(s => (
          <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? "bg-[#0ea5e9] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{s || "All"}</button>
        ))}
        <div className="flex-1 flex gap-2 min-w-[220px]">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") load(); }}
            placeholder="Search by ticket ID / buyer / tier / order..."
            className="flex-1 px-3 py-1.5 rounded-full border text-sm"
            data-testid="shop-admin-ticket-search-input"
          />
          <button onClick={load} className="px-3 py-1.5 rounded-full bg-[#1e3a5f] text-white text-xs font-medium" data-testid="shop-admin-ticket-search-btn">Search</button>
        </div>
      </div>
      {tickets.length === 0 && (
        <div className="bg-white rounded-2xl p-8 text-center text-slate-400 text-sm">No tickets</div>
      )}
      <div className="space-y-3">
        {groupKeys.map(eid => {
          const ev = eventMap[eid];
          const evTickets = grouped[eid];
          const isCollapsed = collapsedEvents[eid];
          const total = evTickets.reduce((s, t) => s + (t.net_revenue ?? t.amount_paid ?? 0), 0);
          return (
            <div key={eid} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`ticket-event-group-${eid}`}>
              <div className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                <button
                  onClick={() => setCollapsedEvents(c => ({ ...c, [eid]: !c[eid] }))}
                  className="flex-1 text-left hover:opacity-80 transition-opacity"
                >
                  <p className="font-bold text-[#1e3a5f]">{ev?.name || "Unknown event"}</p>
                  <p className="text-xs text-slate-500">{ev?.start_date} · {evTickets.length} ticket(s) · €{total.toFixed(2)}</p>
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); exportEvent(eid, ev?.name); }}
                    disabled={exportingId === eid || evTickets.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#2d8a4e] text-white text-xs font-medium disabled:opacity-40"
                    data-testid={`shop-admin-export-event-${eid}`}
                    title="Download Excel"
                  >
                    <Download className="w-3.5 h-3.5" /> {exportingId === eid ? "..." : "Excel"}
                  </button>
                  <button
                    onClick={() => setCollapsedEvents(c => ({ ...c, [eid]: !c[eid] }))}
                    className="text-slate-400 text-xs"
                  >{isCollapsed ? "▶" : "▼"}</button>
                </div>
              </div>
              {!isCollapsed && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500">
                    <tr><th className="text-left p-3">Ticket ID</th><th className="text-left p-3">Date</th><th className="text-left p-3">Buyer</th><th className="text-left p-3">Tier / Package</th><th className="text-right p-3">€</th><th className="text-left p-3">Status</th></tr>
                  </thead>
                  <tbody>
                    {evTickets.map(t => (
                      <tr key={t.id} className="border-t border-slate-100" data-testid={`shop-admin-ticket-row-${t.id}`}>
                        <td className="p-3 font-mono text-xs text-slate-700" title={t.id} data-testid={`shop-admin-ticket-shortid-${t.id}`}>{(t.id || "").slice(0, 8)}</td>
                        <td className="p-3 text-xs text-slate-500">{t.purchased_at?.slice(0, 10)}</td>
                        <td className="p-3"><p className="font-medium text-[#1e3a5f]">{t.buyer_name}</p><p className="text-xs text-slate-400">{t.buyer_email}</p></td>
                        <td className="p-3">
                          {t.package_name && <span className="text-[10px] bg-[#0ea5e9]/10 text-[#0ea5e9] font-bold px-1.5 py-0.5 rounded mr-1">📦 {t.package_name}</span>}
                          {t.tier_name}
                          {t.variant_name && <span className="text-[10px] text-slate-500 ml-1">· {t.variant_name}</span>}
                        </td>
                        <td className="p-3 text-right"><RefundAmountCell t={t} testid={`shop-admin-ticket-amount-${t.id}`} /></td>
                        <td className="p-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.status === "paid" ? "bg-green-100 text-green-600" : t.status === "used" ? "bg-slate-200 text-slate-600" : "bg-red-100 text-red-600"}`}>{t.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========== Vouchers ==========
const EMPTY_VOUCHER = {
  discount_type: "amount", amount: 10, percent: 10, max_discount: 0, min_cart_value: 0,
  expires_at: "", max_redemptions: 1, code: "", event_ids: [], issued_to_email: "", notes: "",
};

const voucherValueLabel = (v) => {
  if (v.discount_type === "percent") {
    const cap = v.max_discount > 0 ? ` (max €${Number(v.max_discount).toFixed(2)})` : "";
    return `${v.percent}% off${cap}`;
  }
  return `€${Number(v.amount || 0).toFixed(2)}`;
};

function VoucherCard({ v, eventNames, onRevoke, onResend }) {
  const maxr = v.max_redemptions || 1;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`voucher-card-${v.code}`}>
      <div className="flex justify-between items-start">
        <div className="min-w-0">
          <p className="font-mono font-bold text-[#1e3a5f] text-lg">{v.code}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {voucherValueLabel(v)} · {v.issue_reason}
            {v.discount_type === "amount" && maxr === 1 && v.balance !== v.amount && ` · balance €${Number(v.balance || 0).toFixed(2)}`}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Used {v.redemption_count || 0}/{maxr}
            {v.min_cart_value > 0 && ` · min cart €${Number(v.min_cart_value).toFixed(2)}`}
            {v.expires_at && ` · expires ${v.expires_at.slice(0, 10)}`}
          </p>
          {(v.event_ids || []).length > 0 && <p className="text-[11px] text-slate-500 mt-0.5">Events: {v.event_ids.map(id => eventNames[id] || id.slice(0, 6)).join(", ")}</p>}
          {v.issued_to_email && (
            <p className="text-xs text-slate-500 mt-1">
              → {v.issued_to_email}
              {v.email_sent === true && <span className="ml-1 text-green-600">· email sent</span>}
              {v.email_sent === false && <span className="ml-1 text-red-500">· email failed</span>}
            </p>
          )}
          {v.notes && <p className="text-xs text-slate-500 mt-1 italic">{v.notes}</p>}
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.status === "active" ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"}`}>{v.status}</span>
          {v.status === "active" && <button onClick={() => onRevoke(v.id)} className="text-red-400 text-xs hover:underline" data-testid={`voucher-revoke-${v.code}`}>Revoke</button>}
          {v.status === "active" && v.issued_to_email && <button onClick={() => onResend(v.id)} className="text-[#0ea5e9] text-xs hover:underline" data-testid={`voucher-resend-${v.code}`}>Resend email</button>}
        </div>
      </div>
    </div>
  );
}

const vField = "w-full px-3 py-2 rounded-xl border text-sm";
const num = (e) => parseFloat(e.target.value) || 0;

function VoucherForm({ events, onCreate }) {
  const [form, setForm] = useState(EMPTY_VOUCHER);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const isPct = form.discount_type === "percent";
  const toggleEvent = (id) => set({ event_ids: form.event_ids.includes(id) ? form.event_ids.filter(x => x !== id) : [...form.event_ids, id] });
  return (
    <div className="space-y-3" data-testid="voucher-form">
      <div className="grid grid-cols-2 gap-2">
        {[["amount", "Fixed amount (€)"], ["percent", "Percentage of cart"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => set({ discount_type: k })} data-testid={`voucher-type-${k}`}
            className={`py-2 rounded-xl border text-sm font-medium ${form.discount_type === k ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "text-slate-600"}`}>{label}</button>
        ))}
      </div>
      {isPct ? (
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-500">Percentage (%) *</label><input type="number" min="0" max="100" step="0.5" value={form.percent} onChange={e => set({ percent: num(e) })} className={vField} data-testid="voucher-percent-input" /></div>
          <div><label className="text-xs text-slate-500">Max discount (€, 0 = no cap)</label><input type="number" min="0" step="0.01" value={form.max_discount} onChange={e => set({ max_discount: num(e) })} className={vField} data-testid="voucher-max-discount-input" /></div>
        </div>
      ) : (
        <div><label className="text-xs text-slate-500">Amount (€) *</label><input type="number" min="0" step="0.01" value={form.amount} onChange={e => set({ amount: num(e) })} className={vField} data-testid="voucher-amount-input" /></div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-xs text-slate-500">Minimum cart value (€)</label><input type="number" min="0" step="0.01" value={form.min_cart_value} onChange={e => set({ min_cart_value: num(e) })} className={vField} data-testid="voucher-min-cart-input" /></div>
        <div><label className="text-xs text-slate-500">Expiry date</label><input type="date" value={form.expires_at} onChange={e => set({ expires_at: e.target.value })} className={vField} data-testid="voucher-expiry-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-xs text-slate-500">Usage limit (1 = single-use)</label><input type="number" min="1" step="1" value={form.max_redemptions} onChange={e => set({ max_redemptions: parseInt(e.target.value, 10) || 1 })} className={vField} data-testid="voucher-max-redemptions-input" /></div>
        <div><label className="text-xs text-slate-500">Custom code (optional)</label><input value={form.code} onChange={e => set({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") })} maxLength={20} placeholder="e.g. DURGA10" className={`${vField} uppercase font-mono`} data-testid="voucher-code-input" /></div>
      </div>
      <p className="text-[10px] text-slate-400">
        {!isPct && form.max_redemptions <= 1 ? "Single-use fixed amount works like stored credit: any unused balance stays on the code." : "Discount codes lower the price; they are never refunded as cash on cancellation."}
      </p>
      {events.length > 0 && (
        <div>
          <label className="text-xs text-slate-500">Valid for events (none selected = all events)</label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="voucher-events">
            {events.map(ev => (
              <button key={ev.id} type="button" onClick={() => toggleEvent(ev.id)} data-testid={`voucher-event-${ev.id}`}
                className={`px-2.5 py-1 rounded-full border text-xs ${form.event_ids.includes(ev.id) ? "bg-[#0ea5e9] text-white border-[#0ea5e9]" : "text-slate-600"}`}>{ev.name}</button>
            ))}
          </div>
        </div>
      )}
      <div><label className="text-xs text-slate-500">Assign to email (optional — the voucher is emailed to this address)</label><input type="email" value={form.issued_to_email} onChange={e => set({ issued_to_email: e.target.value })} className={vField} data-testid="voucher-email-input" /></div>
      <div><label className="text-xs text-slate-500">Notes</label><input value={form.notes} onChange={e => set({ notes: e.target.value })} className={vField} data-testid="voucher-notes-input" /></div>
      <button onClick={() => onCreate(form)} className="w-full py-3 rounded-full bg-[#0ea5e9] text-white font-bold" data-testid="voucher-create-btn">Create</button>
    </div>
  );
}

function VouchersTab({ authAxios }) {
  const [vouchers, setVouchers] = useState([]);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => authAxios.get("/shop-admin/vouchers").then(r => setVouchers(r.data));
  useEffect(() => {
    load();
    authAxios.get("/shop-admin/events").then(r => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []); // eslint-disable-line
  const eventNames = Object.fromEntries(events.map(e => [e.id, e.name]));

  const create = async (form) => {
    if (form.discount_type === "amount" && !form.amount) return toast.error("Amount required");
    if (form.discount_type === "percent" && !form.percent) return toast.error("Percentage required");
    try {
      const res = await authAxios.post("/shop-admin/vouchers", form);
      toast.success(`Voucher created: ${res.data.code}`);
      if (res.data.email_sent === true) toast.success(`Email sent to ${res.data.issued_to_email}`);
      if (res.data.email_sent === false) toast.error(`Voucher created but the email to ${res.data.issued_to_email} could not be sent`);
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const revoke = async (id) => {
    try { await authAxios.post(`/shop-admin/vouchers/${id}/revoke`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const resend = async (id) => {
    try {
      const r = await authAxios.post(`/shop-admin/vouchers/${id}/resend-email`);
      if (r.data.email_sent) toast.success("Email sent"); else toast.error("Email could not be sent");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-[#1e3a5f]">Shop Vouchers ({vouchers.length})</h2>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#0ea5e9] text-white text-sm font-medium" data-testid="add-voucher-btn"><Plus className="w-4 h-4" /> New Voucher</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {vouchers.map(v => <VoucherCard key={v.id} v={v} eventNames={eventNames} onRevoke={revoke} onResend={resend} />)}
        {vouchers.length === 0 && <p className="text-slate-400 text-sm text-center col-span-2 py-8">No vouchers yet</p>}
      </div>
      {showForm && (
        <Modal title="New Voucher" onClose={() => setShowForm(false)}>
          <VoucherForm events={events} onCreate={create} />
        </Modal>
      )}
    </div>
  );
}


// ========== Packages ==========
function PackagesTab({ authAxios }) {
  const [events, setEvents] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const fetchAll = async () => {
    const [ev, tt, mt, pk] = await Promise.all([
      authAxios.get("/shop-admin/events"),
      authAxios.get("/shop-admin/ticket-types" + (selectedEvent ? `?event_id=${selectedEvent}` : "")),
      authAxios.get("/ticketing/public/member-types").catch(() => ({ data: [] })),
      authAxios.get("/shop-admin/packages" + (selectedEvent ? `?event_id=${selectedEvent}` : "")),
    ]);
    setEvents(ev.data); setTiers(tt.data); setMemberTypes(mt.data); setPackages(pk.data);
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [selectedEvent]);

  const openNew = () => {
    setEditing(null);
    // Default audience: Unregistered + Guest + all currently-defined member types.
    const defaultAudience = ["unregistered", "guest", ...(memberTypes || []).map(m => m.id)];
    setForm({ event_id: selectedEvent || (events[0]?.id || ""), name: "", description: "", banner_url: "", tier_ids: [], audience: defaultAudience, discount: null, member_prices: [], is_active: true, sort_order: 0, category: "", category_sort_order: 0 });
    setShowForm(true);
  };
  const openEdit = (p) => {
    // Cleanup stale tier_ids that reference deleted tiers so the admin's checkboxes
    // reflect the live state and the package can be saved without hitting the
    // "All tiers must exist and belong to the event" 400.
    const validTierIds = new Set((tiers || []).filter(t => t.event_id === p.event_id).map(t => t.id));
    const cleanedTierIds = (p.tier_ids || []).filter(tid => validTierIds.has(tid));
    // Legacy "member" audience — expand to per-member-type ids so AudienceAndPricingEditor
    // can express the same intent (it has no "all members" row).
    const rawAud = p.audience || [];
    let cleanedAudience = rawAud.includes("member")
      ? Array.from(new Set([...rawAud.filter(a => a !== "member"), ...(memberTypes || []).map(m => m.id)]))
      : rawAud;
    // Legacy items (audience_v < 2) implicitly include "unregistered" — surface that in
    // the UI so admin sees what's actually happening on the public shop.
    if ((p.audience_v || 1) < 2 && !cleanedAudience.includes("unregistered")) {
      cleanedAudience = ["unregistered", ...cleanedAudience];
    }
    setEditing(p);
    setForm({
      ...p,
      tier_ids: cleanedTierIds,
      audience: cleanedAudience,
      discount: p.discount || null,
      member_prices: p.member_prices || [],
    });
    setShowForm(true);
  };
  const toggleActive = async (p) => {
    try {
      await authAxios.put(`/shop-admin/packages/${p.id}`, { is_active: !p.is_active });
      toast.success(`${p.name} ${!p.is_active ? "activated" : "deactivated"}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const save = async () => {
    if (!form.event_id || !form.name || !form.tier_ids?.length || !form.audience?.length) return toast.error("Event, name, tiers and audience required");
    try {
      if (editing) await authAxios.put(`/shop-admin/packages/${editing.id}`, form);
      else await authAxios.post("/shop-admin/packages", form);
      toast.success("Saved");
      setShowForm(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    try { await authAxios.delete(`/shop-admin/packages/${id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const toggleTier = (id) => setForm(f => ({ ...f, tier_ids: f.tier_ids.includes(id) ? f.tier_ids.filter(x => x !== id) : [...f.tier_ids, id] }));

  // Reorder by swapping sort_order with the adjacent package in the same
  // category. Handles ties by nudging one value up/down.
  const movePackage = async (groupItems, idx, direction) => {
    const other = direction === "up" ? idx - 1 : idx + 1;
    if (other < 0 || other >= groupItems.length) return;
    const a = groupItems[idx];
    const b = groupItems[other];
    const aOrder = Number.isFinite(a.sort_order) ? a.sort_order : 0;
    const bOrder = Number.isFinite(b.sort_order) ? b.sort_order : 0;
    let newA, newB;
    if (aOrder !== bOrder) {
      newA = bOrder; newB = aOrder;
    } else {
      newA = direction === "up" ? aOrder - 1 : aOrder + 1;
      newB = bOrder;
    }
    try {
      await Promise.all([
        authAxios.put(`/shop-admin/packages/${a.id}`, { sort_order: newA }),
        authAxios.put(`/shop-admin/packages/${b.id}`, { sort_order: newB }),
      ]);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Reorder failed"); }
  };

  const eventTiers = tiers.filter(t => t.event_id === form.event_id);
  const priceSum = eventTiers.filter(t => form.tier_ids?.includes(t.id)).reduce((s, t) => s + (t.price || 0), 0);

  return (
    <div data-testid="packages-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)} className="px-3 py-2 rounded-xl border text-sm">
          <option value="">All events</option>
          {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <button onClick={openNew} className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#0ea5e9] text-white text-sm font-medium" data-testid="add-package-btn">
          <Plus className="w-4 h-4" /> New Package
        </button>
      </div>
      <div className="space-y-5">
        {groupByCategory(packages).map(group => (
          <section key={group.key} data-testid={`package-group-${group.key}`}>
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 px-1" data-testid={`package-group-label-${group.key}`}>
              {group.label}
              <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case">{group.items.length} item{group.items.length !== 1 ? "s" : ""}</span>
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {group.items.map((p, idx) => {
          const aud = p.audience || [];
          const memberTypeIds = new Set((memberTypes || []).map(m => m.id));
          const showUnreg = aud.includes("unregistered") || (p.audience_v || 1) < 2;
          const showGuest = aud.includes("guest");
          const showMember = aud.includes("member") || aud.some(a => memberTypeIds.has(a));
          const audPills = [];
          if (showUnreg) audPills.push({ key: "unregistered", label: "Unregistered", cls: "bg-slate-200 text-slate-700" });
          if (showGuest) audPills.push({ key: "guest", label: "Guest", cls: "bg-amber-100 text-amber-700" });
          if (showMember) audPills.push({ key: "member", label: "Member", cls: "bg-blue-100 text-blue-700" });
          return (
          <div key={p.id} className="bg-white rounded-2xl p-4 shadow-sm relative" data-testid={`package-card-${p.id}`}>
            <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-slate-50 rounded-full border border-slate-200 px-1 py-0.5 shadow-sm" data-testid={`package-reorder-${p.id}`}>
              <button
                onClick={() => movePackage(group.items, idx, "up")}
                disabled={idx === 0}
                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 hover:text-[#0ea5e9] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up within category"
                data-testid={`package-move-up-${p.id}`}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => movePackage(group.items, idx, "down")}
                disabled={idx === group.items.length - 1}
                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 hover:text-[#0ea5e9] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down within category"
                data-testid={`package-move-down-${p.id}`}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex justify-between items-start gap-2 pt-6">
              <div className="flex-1">
                <h4 className="font-bold text-[#1e3a5f]">{p.name}</h4>
                <p className="text-xs text-slate-500 mt-1">{events.find(e => e.id === p.event_id)?.name}</p>
                <p className="text-xs text-slate-600 mt-1">{p.tier_ids?.length || 0} tier(s) included</p>
                {p.discount && <p className="text-xs text-[#2d8a4e] mt-1 font-medium">Discount: {p.discount.type === "percent" ? `${p.discount.value}%` : `€${p.discount.value}`}</p>}
                {(p.tier_issues || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`package-issues-${p.id}`}>
                    {p.tier_issues.map(i => (
                      <span
                        key={`${i.tier_id}-${i.reason}`}
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${i.reason === "sold_out" ? "bg-red-100 text-red-700" : i.reason === "inactive" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}
                      >
                        ⚠ {i.tier_name} — {i.reason === "sold_out" ? "sold out" : i.reason === "inactive" ? "inactive tier" : "removed"}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-2" data-testid={`package-audience-${p.id}`}>
                  {audPills.map(a => (
                    <span key={a.key} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${a.cls}`} data-testid={`package-audience-pill-${p.id}-${a.key}`}>{a.label}</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <button
                  onClick={() => toggleActive(p)}
                  className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full transition-colors ${p.is_active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}
                  data-testid={`package-active-toggle-${p.id}`}
                  title={p.is_active ? "Click to deactivate" : "Click to activate"}
                >
                  <span className={`w-2 h-2 rounded-full ${p.is_active ? "bg-green-500" : "bg-slate-400"}`} />
                  {p.is_active ? "Active" : "Inactive"}
                </button>
                <button onClick={() => openEdit(p)} className="text-blue-500" data-testid={`package-edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} className="text-red-400" data-testid={`package-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
          );
        })}
            </div>
          </section>
        ))}
        {packages.length === 0 && <p className="text-slate-400 text-sm text-center py-8">No packages yet</p>}
      </div>
      {showForm && (
        <Modal title={editing ? "Edit Package" : "New Package"} onClose={() => setShowForm(false)}>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Event *</label>
              <select value={form.event_id} onChange={e => setForm({ ...form, event_id: e.target.value, tier_ids: [] })} className="w-full px-3 py-2 rounded-xl border text-sm" disabled={!!editing}>
                <option value="">Select event</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-slate-500">Package Name *</label><input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" placeholder="e.g. Weekend Combo" data-testid="package-name-input" /></div>
            <div><label className="text-xs text-slate-500">Description</label><textarea value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
            <BannerUpload authAxios={authAxios} value={form.banner_url} onChange={url => setForm({ ...form, banner_url: url })} purpose="package-banner" label="Package Banner (optional)" />
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Include Ticket Tiers *</label>
              <div className="border rounded-xl p-2 max-h-40 overflow-y-auto space-y-1">
                {eventTiers.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Select an event to see tiers</p>}
                {eventTiers.map(t => (
                  <label key={t.id} className="flex items-center gap-2 text-sm p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                    <input type="checkbox" checked={form.tier_ids?.includes(t.id)} onChange={() => toggleTier(t.id)} />
                    <span className="flex-1">{t.name}</span>
                    <span className="text-slate-500 text-xs">€{t.price?.toFixed(2)}</span>
                  </label>
                ))}
              </div>
              {form.tier_ids?.length > 0 && <p className="text-xs text-slate-500 mt-1">Sum of tier prices: <strong>€{priceSum.toFixed(2)}</strong></p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Guest discount (applied to the summed tier price)</label>
              <DiscountEditor value={form.discount} onChange={d => setForm({ ...form, discount: d })} />
            </div>
            {/* iter160: display order — lower = shown first on public shop. */}
            <div>
              <label className="text-xs text-slate-500">Display order (lower = shown first)</label>
              <input
                type="number"
                value={form.sort_order ?? 0}
                onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
                className="w-full px-3 py-2 rounded-xl border text-sm"
                placeholder="0"
                data-testid="package-sort-order-input"
              />
            </div>
            {/* iter162: category grouping for packages (parallel to tiers). */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Category (optional)</label>
                <input
                  type="text"
                  value={form.category || ""}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  placeholder="e.g. Weekend Combo"
                  data-testid="package-category-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Category order</label>
                <input
                  type="number"
                  value={form.category_sort_order ?? 0}
                  onChange={e => setForm({ ...form, category_sort_order: parseInt(e.target.value || "0", 10) })}
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  placeholder="0"
                  data-testid="package-category-sort-order-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Audience & Pricing *</label>
              <AudienceAndPricingEditor
                audience={form.audience || []}
                memberPrices={form.member_prices || []}
                memberTypes={memberTypes}
                basePrice={priceSum}
                baseDiscount={form.discount}
                onChange={({ audience, memberPrices }) => setForm(f => ({ ...f, audience, member_prices: memberPrices }))}
              />
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            <button onClick={save} className="w-full py-3 rounded-full bg-[#0ea5e9] text-white font-bold" data-testid="save-package-btn">{editing ? "Update" : "Create"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
